import { defineStore } from 'pinia'

import { ShowImageCrop, StoreShow, parseShow } from '@/models'
import ShowService from '@/modules/show/services/ShowService'
import { byId, deleteById, sortByName, upsertById } from '@/utils/utils'

import { ShowCropWrite, ShowDetailedWrite, ShowRead } from '@generated/types'

export interface ShowState {
  shows: StoreShow[]
}

export const useShowStore = defineStore('show', {
  state: (): ShowState => {
    return {
      shows: [],
    }
  },
  actions: {
    sortShows() {
      sortByName(this.shows)
    },

    showById(id: number): StoreShow {
      let show = this.shows.find(byId(id))
      if (!show) {
        throw new Error(`Show ${id} is not found`)
      }
      return show
    },

    async fetchShows(searchQuery?: string): Promise<void> {
      const response = await ShowService.fetchAll(searchQuery)
      this.shows = response.data.map(parseShow)
      this.sortShows()
    },

    async fetchShow(id: number): Promise<StoreShow> {
      const response = await ShowService.fetchOne(id)
      return this.storeShow(response.data)
    },

    storeShow(showRead: ShowRead): StoreShow {
      const show = parseShow(showRead)
      upsertById(show, this.shows)
      this.sortShows()
      return show
    },

    async upsertShow(showWrite: ShowDetailedWrite): Promise<StoreShow> {
      const response = !showWrite.id
        ? await ShowService.create(showWrite)
        : await ShowService.update(showWrite)
      return this.storeShow(response.data)
    },

    async updateShowImage({ id, image }): Promise<StoreShow> {
      let formData = new FormData()
      formData.append('file', image)
      const response = await ShowService.updateImage(id, formData)
      return this.storeShow(response.data)
    },

    async updateShowImageCrop({
      id,
      crops,
    }: {
      id: number
      crop: ShowImageCrop
      crops: ShowCropWrite
    }): Promise<StoreShow> {
      const response = await ShowService.updateCrops(id, crops)
      return this.storeShow(response.data)
    },

    async deleteShow(id: number): Promise<void> {
      await ShowService.delete(id)
      deleteById(id, this.shows)
    },

    async duplicateShow(id: number): Promise<StoreShow> {
      const response = await ShowService.duplicate(id)
      return this.storeShow(response.data)
    },
  },
})
